/**
 * Native bootstrap.
 *
 * Runs once at app start. On web: no-op.
 * On native: hydrates sessionStorage from Capacitor Preferences (so the
 * user stays logged in across cold-starts) and mirrors any future
 * sessionStorage writes back to Preferences automatically. This means
 * the existing 13+ sessionStorage usages across the codebase need ZERO
 * changes.
 *
 * Build 23 / v2.5.2 (2026-06-21) — P0 root-cause fix.
 * Every native plugin call is now wrapped in `withTimeout(...)` so a
 * single stuck Capacitor bridge can NEVER hang the boot. The exported
 * `nativeBootstrapReady` Promise is guaranteed to resolve within
 * ~2s (worst case, all timeouts hit). `index.js` no longer awaits
 * this Promise — React mounts immediately — but downstream code
 * (e.g. AuthProvider.checkAuth) can await it if it wants a
 * best-effort session hydration.
 */
import { Preferences } from '@capacitor/preferences';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isNative } from './platform';

const PERSISTED_KEYS = [
  'session_token',
  'user',
  'vendor_session',
  'admin_session',
  'login_timestamp',
  'intendedPath',
  'washpros_pending_booking',
  'portal_switch_intent',
];

// Per-call plugin timeout. Individual reads/writes must resolve
// within this budget or we abandon them so the boot keeps moving.
const NATIVE_CALL_TIMEOUT_MS = 1500;
// Overall hydration budget across all PERSISTED_KEYS reads. Enforced
// via Promise.race so even if a single call's timeout misfires (JS
// event-loop starvation) the whole hydration returns.
const HYDRATE_TOTAL_BUDGET_MS = 2500;

let _bootstrapped = false;

// Phase 7g.15.137 (2026-06-20, P0 Android root-cause fix) — Track all
// in-flight Preferences writes so callers about to perform a hard
// navigation (e.g. `window.location.href = "/admin"` after login)
// can `await flushNativePreferences()` and be sure the mirror
// completed before the WebView tears the JS context down.
const _pendingPreferenceWrites = new Set();

const _trackPreferenceWrite = (promise) => {
  _pendingPreferenceWrites.add(promise);
  const cleanup = () => _pendingPreferenceWrites.delete(promise);
  promise.then(cleanup, cleanup);
};

/**
 * withTimeout — Race the given promise against a hard timeout. If
 * the promise settles first, its outcome is returned. If the timeout
 * wins, a synthetic { __timeout: true, label } object is returned
 * (never rejects) so the caller can continue without a try/catch.
 */
const withTimeout = (promise, ms, label) => {
  return new Promise((resolve) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      try {
        console.warn(`[WP_BOOT] timeout: ${label} exceeded ${ms}ms — abandoning`);
      } catch (_e) { /* noop */ }
      resolve({ __timeout: true, label });
    }, ms);
    Promise.resolve(promise).then(
      (value) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        try {
          console.warn(`[WP_BOOT] plugin rejected: ${label}`, err);
        } catch (_e) { /* noop */ }
        resolve({ __error: true, label, err });
      },
    );
  });
};

export const flushNativePreferences = async () => {
  if (!isNative()) return;
  if (_pendingPreferenceWrites.size === 0) return;
  // Wait for all pending writes; allSettled so one rejection
  // doesn't prevent the others from being awaited. Cap the flush
  // at 1s so a hung write can never block a navigation.
  await withTimeout(
    Promise.allSettled(Array.from(_pendingPreferenceWrites)),
    1000,
    'flushNativePreferences',
  );
};

const wrapSessionStorage = () => {
  if (typeof sessionStorage === 'undefined') return;
  const _setItem = sessionStorage.setItem.bind(sessionStorage);
  const _removeItem = sessionStorage.removeItem.bind(sessionStorage);
  const _clear = sessionStorage.clear.bind(sessionStorage);

  sessionStorage.setItem = function (key, value) {
    _setItem(key, value);
    if (PERSISTED_KEYS.includes(key)) {
      const p = withTimeout(
        Preferences.set({ key, value: String(value) }),
        NATIVE_CALL_TIMEOUT_MS,
        `Preferences.set(${key})`,
      );
      _trackPreferenceWrite(p);
    }
  };
  sessionStorage.removeItem = function (key) {
    _removeItem(key);
    if (PERSISTED_KEYS.includes(key)) {
      const p = withTimeout(
        Preferences.remove({ key }),
        NATIVE_CALL_TIMEOUT_MS,
        `Preferences.remove(${key})`,
      );
      _trackPreferenceWrite(p);
    }
  };
  sessionStorage.clear = function () {
    _clear();
    const p = withTimeout(
      Preferences.clear(),
      NATIVE_CALL_TIMEOUT_MS,
      'Preferences.clear',
    );
    _trackPreferenceWrite(p);
  };
};

/**
 * Hydrate sessionStorage from Capacitor Preferences, then install the
 * mirror so all future writes get persisted automatically.
 *
 * Guaranteed to settle within HYDRATE_TOTAL_BUDGET_MS + a small
 * epsilon regardless of native plugin behavior. Never throws.
 */
const _doBootstrap = async () => {
  if (_bootstrapped || !isNative()) return;
  _bootstrapped = true;

  try {
    console.info('[WP_BOOT] bootstrapNative start');
  } catch (_e) { /* noop */ }

  // 1. Hydrate — race a per-key read against the individual timeout,
  //    then race the whole batch against the overall budget. Any
  //    key that fails to resolve is quietly skipped.
  const hydrateAll = (async () => {
    for (const key of PERSISTED_KEYS) {
      const outcome = await withTimeout(
        Preferences.get({ key }),
        NATIVE_CALL_TIMEOUT_MS,
        `Preferences.get(${key})`,
      );
      if (!outcome || outcome.__timeout || outcome.__error) continue;
      const value = outcome.value;
      if (value != null && typeof sessionStorage !== 'undefined' && !sessionStorage.getItem(key)) {
        try {
          // Bypass the wrapper so hydration doesn't schedule a
          // redundant mirror write for the same value.
          sessionStorage.setItem(key, value);
        } catch (_e) { /* noop */ }
      }
    }
  })();
  await withTimeout(hydrateAll, HYDRATE_TOTAL_BUDGET_MS, 'hydrateAll');

  // 2. Install mirror on top of native sessionStorage. Do this AFTER
  //    hydration so the mirror doesn't fire for the hydration writes.
  try {
    wrapSessionStorage();
  } catch (e) {
    try { console.warn('[WP_BOOT] wrapSessionStorage failed', e); } catch (_e) { /* noop */ }
  }

  // 3. Mark this bundle as healthy so the OTA plugin doesn't
  //    auto-revert. Also timeout-wrapped — a hang here used to
  //    prevent React from mounting before the Build 23 fix.
  await withTimeout(
    CapacitorUpdater.notifyAppReady(),
    NATIVE_CALL_TIMEOUT_MS,
    'CapacitorUpdater.notifyAppReady',
  );

  try {
    console.info('[WP_BOOT] bootstrapNative done');
  } catch (_e) { /* noop */ }
};

// Exported Promise that resolves once native hydration + mirror
// install have finished (or timed out). Never rejects. Consumers
// awaiting this get a best-effort session hydration signal.
export let nativeBootstrapReady = Promise.resolve();

export const bootstrapNative = () => {
  if (!isNative()) {
    return Promise.resolve();
  }
  if (_bootstrapped) {
    return nativeBootstrapReady;
  }
  // Wrap the entire bootstrap in a hard outer timeout too, so even
  // if every inner timeout misfires the promise cannot hang past
  // ~4s total. Never rejects.
  nativeBootstrapReady = withTimeout(_doBootstrap(), 4000, 'bootstrapNative_outer');
  return nativeBootstrapReady;
};
